import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col,
    Input, InputGroup
} from "reactstrap";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import DataTable from "react-data-table-component";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import { FileText, Download } from 'react-feather';
import DatePicker from "react-datepicker";
import { useConfig } from "../../Context/ConfigContext";
import { useAuth } from "../../Context/AuthContext";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDuration = (minutes: number) => {
    if (!minutes) return "0m";
    const h = Math.floor(minutes / 60);
    const m = Math.floor(minutes % 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
};

interface BystanderPerformanceData {
    bystander_id: number;
    bystander_name: string;
    bystander_mobile: string;
    avg_rating: number;
    total_bookings: number;
    total_expired: number;
    total_rejected: number;
    total_reassignments: number;
    total_duration_minutes: number;
    revenue_generated: number;
    bystander_earnings: number;
}

interface PerformanceDataWithFooter extends Partial<BystanderPerformanceData> {
    isFooter?: boolean;
}

export default function BystanderPerformanceReport() {
    const [data, setData] = useState<BystanderPerformanceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("total_bookings");
    const [sortDir, setSortDir] = useState("desc");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [summary, setSummary] = useState({
        total_bookings: 0,
        total_expired: 0,
        total_rejected: 0,
        total_reassignments: 0,
        total_duration_minutes: 0,
        revenue_generated: 0,
        bystander_earnings: 0
    });
    const [pdfLoading, setPdfLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const { config } = useConfig();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const response: any = await apiPost(`report/bystander-performance-report?page=${currentPage}`, {
                per_page: perPage,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            setData(response.data.data || []);
            setTotalRows(response.data.total || 0);
            setSummary(response.summary || {
                total_bookings: 0,
                total_expired: 0,
                total_rejected: 0,
                total_reassignments: 0,
                total_duration_minutes: 0,
                revenue_generated: 0,
                bystander_earnings: 0
            });
        } catch (error) {
            toast.error("Failed to load bystander performance report");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, debouncedSearch, startDate, endDate, sortBy, sortDir]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, startDate, endDate]);

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || 'total_bookings');
        setSortDir(sortDirection);
    };

    const fetchAllData = async () => {
        try {
            const response: any = await apiPost(`report/bystander-performance-report?page=1`, {
                per_page: -1,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            return response.data.data || [];
        } catch (error) {
            console.error("Error fetching all data:", error);
            return [];
        }
    };

    const exportToExcel = async (exportDataItems: any[]) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Bystander Performance');

        const headerStyle: Partial<ExcelJS.Style> = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00695C' } },
            alignment: { vertical: 'middle', horizontal: 'center' }
        };

        worksheet.columns = [
            { header: 'Sl.No', key: 'sl', width: 8 },
            { header: 'Bystander Name', key: 'name', width: 30 },
            { header: 'Rating', key: 'rating', width: 10 },
            { header: 'Completed', key: 'bookings', width: 15 },
            { header: 'Expired', key: 'expired', width: 12 },
            { header: 'Rejected', key: 'rejected', width: 12 },
            { header: 'Reassign Req', key: 'reassign', width: 15 },
            { header: 'Total Duration', key: 'duration', width: 15 },
            { header: `Revenue (${config.currency})`, key: 'revenue', width: 20 },
            { header: `Earnings (${config.currency})`, key: 'earnings', width: 15 },
        ];

        worksheet.getRow(1).eachCell((cell) => {
            cell.style = headerStyle;
        });

        exportDataItems.forEach((item, index) => {
            const row = worksheet.addRow({
                sl: index + 1,
                name: `${item.bystander_name || "N/A"}\n${item.bystander_mobile || ""}`,
                rating: item.avg_rating || 0,
                bookings: Number(item.total_bookings || 0),
                expired: Number(item.total_expired || 0),
                rejected: Number(item.total_rejected || 0),
                reassign: Number(item.total_reassignments || 0),
                duration: formatDuration(Number(item.total_duration_minutes || 0)),
                revenue: Number(item.revenue_generated || 0),
                earnings: Number(item.bystander_earnings || 0)
            });

            row.eachCell((cell) => {
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
        });

        const summaryRow = worksheet.addRow({
            sl: '',
            name: 'TOTAL',
            bookings: summary.total_bookings,
            expired: summary.total_expired,
            rejected: summary.total_rejected,
            reassign: summary.total_reassignments,
            duration: formatDuration(Number(summary.total_duration_minutes || 0)),
            revenue: summary.revenue_generated,
            earnings: summary.bystander_earnings
        });

        summaryRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Bystander_Performance_Report_${formatDate(new Date())}.xlsx`);
    };

    const exportToPDF = (exportDataItems: any[]) => {
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.text("Bystander Performance Report", 14, 15);

        const currencyMap: Record<string, string> = {
            '₹': 'Rs.', '€': 'EUR', '£': 'GBP', '¥': 'YEN',
            '₽': 'RUB', '₩': 'KRW', '฿': 'THB', '₪': 'ILS', '₫': 'VND', '₱': 'PHP'
        };
        const pdfCurrency = currencyMap[config.currency] || config.currency;
        const tableColumn = ["Sl.No", "Bystander Info", "Rating", "Done", "Exp", "Rej", "Swap", "Duration", `Rev (${pdfCurrency})`, `Earn (${pdfCurrency})`];
        const tableRows = exportDataItems.map((item, index) => {
            return [
                index + 1,
                `${item.bystander_name || "N/A"}\n${item.bystander_mobile || ""}`,
                item.avg_rating || 0,
                item.total_bookings,
                item.total_expired,
                item.total_rejected,
                item.total_reassignments,
                formatDuration(Number(item.total_duration_minutes || 0)),
                Number(item.revenue_generated || 0).toFixed(2),
                Number(item.bystander_earnings || 0).toFixed(2)
            ];
        });

        tableRows.push([
            "",
            "TOTAL",
            "",
            String(summary.total_bookings || 0),
            String(summary.total_expired || 0),
            String(summary.total_rejected || 0),
            String(summary.total_reassignments || 0),
            formatDuration(Number(summary.total_duration_minutes || 0)),
            Number(summary.revenue_generated || 0).toFixed(2),
            Number(summary.bystander_earnings || 0).toFixed(2)
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: 'grid',
            headStyles: { fillColor: [0, 105, 92] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 15 },
                2: { halign: 'center' },
                3: { halign: 'center' },
                4: { halign: 'right', textColor: [211, 47, 47] },
                5: { fontStyle: 'bold', halign: 'right', textColor: [0, 105, 92] }
            },
            didParseCell: (cellData: any) => {
                if (cellData.row.index === tableRows.length - 1) {
                    cellData.cell.styles.fontStyle = 'bold';
                    cellData.cell.styles.fillColor = [245, 245, 245];
                }
            }
        });
        doc.save(`Bystander_Performance_Report_${formatDate(new Date())}.pdf`);
    };

    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: PerformanceDataWithFooter, index: number) =>
                row.isFooter ? "" : (currentPage - 1) * perPage + (index ?? 0) + 1,
            width: "80px",
        },
        {
            name: <div><span className="fw-bold">Bystander Info</span></div>,
            cell: (row: PerformanceDataWithFooter) => {
                if (row.isFooter) return "";
                return (
                    <div className="py-2">
                        <div className="fw-bold text-dark">{row.bystander_name || "N/A"}</div>
                        <div className="small text-muted">{row.bystander_mobile || ""}</div>
                    </div>
                );
            },
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "bystander_name", wrap: true, width: "200px"
        },
        {
            name: <div><span className="fw-bold">Rating</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "avg_rating",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-warning" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {row.isFooter ? <span className="fw-bold" style={{ fontSize: '14px', color: '#00695c' }}>TOTAL:</span> : (row.avg_rating ? `⭐ ${row.avg_rating}` : "No Ratings")}
                </div>
            ),
        },
        {
            name: <div className="text-center w-100"><span className="fw-bold">Done</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "total_bookings",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-center text-primary">
                    {row.total_bookings}
                </div>
            ),
        },
        {
            name: <div className="text-center w-100"><span className="fw-bold text-secondary">Expired</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "total_expired",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-center text-secondary">
                    {row.total_expired}
                </div>
            ),
        },
        {
            name: <div className="text-center w-100"><span className="fw-bold text-warning">Rejected</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "total_rejected",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-center text-warning">
                    {row.total_rejected}
                </div>
            ),
        },
        {
            name: <div className="text-center w-100"><span className="fw-bold text-info">Swap</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "total_reassignments",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-center text-info">
                    {row.total_reassignments}
                </div>
            ),
        },
        {
            name: <div className=" w-100"><span className="fw-bold">Total Duration</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "total_duration_minutes",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-info  px-2" style={{ color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {formatDuration(Number(row.total_duration_minutes || 0))}
                </div>
            )
        },
        {
            name: <div className=" w-100"><span className="fw-bold">Revenue Generated ({config.currency})</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "revenue_generated",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-danger  px-2" style={{ color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.revenue_generated || 0).toLocaleString()}
                </div>
            )
        },
        {
            name: <div className=" w-100"><span className="fw-bold">Earnings ({config.currency})</span></div>,
            sortable: (row: PerformanceDataWithFooter) => !row.isFooter,
            sortField: "bystander_earnings",
            cell: (row: PerformanceDataWithFooter) => (
                <div className="w-100 fw-bold text-success  px-2" style={{ color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.bystander_earnings || 0).toLocaleString()}
                </div>
            )
        },
    ];

    const columnsData: PerformanceDataWithFooter[] = data.length > 0 ? [
        ...data,
        {
            isFooter: true,
            total_bookings: summary.total_bookings,
            total_expired: summary.total_expired,
            total_rejected: summary.total_rejected,
            total_reassignments: summary.total_reassignments,
            total_duration_minutes: summary.total_duration_minutes,
            revenue_generated: summary.revenue_generated,
            bystander_earnings: summary.bystander_earnings
        }
    ] : [];

    const conditionalRowStyles = [
        {
            when: (row: PerformanceDataWithFooter) => !!row.isFooter,
            style: {
                fontWeight: "800",
                backgroundColor: "#f8f9fa",
                color: "#1e3a8a",
                borderTop: "2px solid #dee2e6",
                fontSize: "16px",
            },
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                color: '#308e87',
                minHeight: '50px',
                borderBottom: '2px solid #308e87',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px', borderBottom: "1px solid #dee2e6" },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Bystander Performance Report" parent="Reports" title="Audit Stream" />
            <Container fluid>
                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Bystander Performance Report</H3>
                            <div className="d-flex gap-2">
                                {(hasPermission('bystander-performance.download_pdf') || hasPermission('bystander-performance-report.download_pdf')) && (
                                    <Button
                                        color="success"
                                        onClick={async () => {
                                            setPdfLoading(true);
                                            const allData = await fetchAllData();
                                            exportToPDF(allData);
                                            setPdfLoading(false);
                                        }}
                                        disabled={pdfLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0"
                                        style={{ borderRadius: '6px' }}
                                        title="Export PDF"
                                    >
                                        {pdfLoading ? <Spinner size="sm" /> : <FileText size={16} />}
                                        <span className="fw-bold small">{pdfLoading ? 'PREPARING...' : 'PDF'}</span>
                                    </Button>
                                )}
                                {(hasPermission('bystander-performance.download_excel') || hasPermission('bystander-performance-report.download_excel')) && (
                                    <Button
                                        color="warning"
                                        onClick={async () => {
                                            setExcelLoading(true);
                                            const allData = await fetchAllData();
                                            await exportToExcel(allData);
                                            setExcelLoading(false);
                                        }}
                                        disabled={excelLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0 text-white"
                                        style={{ backgroundColor: '#ffaf00', borderRadius: '6px' }}
                                        title="Export Excel"
                                    >
                                        {excelLoading ? <Spinner size="sm" /> : <Download size={16} />}
                                        <span className="fw-bold small">{excelLoading ? 'PREPARING...' : 'EXCEL'}</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className=" mb-4">
                            <Row className="align-items-center justify-content-end g-3">
                                <Col xl={5} lg={7} md={12} className="d-flex align-items-center gap-2">
                                    <span className="small text-muted fw-bold text-uppercase ms-2" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>FROM:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={startDate ? new Date(startDate) : null}
                                            onChange={(date: Date) => setStartDate(date ? formatDate(date) : "")}
                                            placeholderText="Start date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                    <span className="small text-muted fw-bold text-uppercase" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>TO:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={endDate ? new Date(endDate) : null}
                                            onChange={(date: Date) => setEndDate(date ? formatDate(date) : "")}
                                            placeholderText="End date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                </Col>

                                <Col xl={3} lg={5} md={12}>
                                    <InputGroup className="shadow-sm">
                                        <Input
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="bg-white"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </div>

                        <div className="border rounded">
                            <DataTable
                                columns={columns}
                                data={columnsData}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangeRowsPerPage={(newSize: number, page: number) => setPerPage(newSize)}
                                onChangePage={(page: number) => setCurrentPage(page)}
                                sortServer
                                onSort={handleSort}
                                highlightOnHover
                                customStyles={customStyles}
                                conditionalRowStyles={conditionalRowStyles}
                                noDataComponent={
                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                        <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                            <CardBody>
                                                <h6 className="fw-semibold mb-1">No Records Found</h6>
                                                <small className="text-muted">No bystander performance data matching your criteria.</small>
                                            </CardBody>
                                        </Card>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
